import React, {useEffect, useRef, useState} from 'react';
import {Form, Input} from 'antd';
import type {ProFormInstance} from '@ant-design/pro-form';
import type {WdiModalAppProps} from '@/components/WdiControls';
import {HorzLayoutOpts, WdiModalForm} from '@/components/WdiControls/WdiModalForm';
import {WdiSelect} from '@/components/WdiControls/WdiSelect';
import {Enum} from '@/enums/system';
import {PageType} from '@/services/basic/pagetype';
import {FormSa} from '@/services/sales/sa';
import {FormApi} from '@/services/sales';
import ProjectSelectApp from '../../components/ProjectCmpt';
import {ResponseStatus} from "@/utils/request";
import {Position} from "@/services/basic/position";
import {RevenueType} from "@/services/mdm/revenuetype";

const MyActivityPrevApp: React.FC<WdiModalAppProps> = (props) => {
  const formRef = useRef<ProFormInstance>();

  const [pageTypes, setPageTypes] = useState<API.PageType[]>();
  const [enable, setEnable] = useState<boolean>(true);
  const [needProject, setNeedProject] = useState(true);

  const handlePageTypeIdChange = (value: any) => {
    if (value) {
      const pageType = pageTypes?.find((item) => item.pageTypeId === value);
      if (pageType) {
        formRef.current?.setFieldsValue({
          remark: pageType.remark,
        });
      }
      setNeedProject(value != Enum.PageType.PRICE_DISCOUNT_APPLICATION);
    }
  };

  useEffect(() => {
    (async () => {
      const pageTypeResp = await PageType.search({
        formTypeId: Enum.FormType.ACTIVITY_APPLICATION,
        pageTypeIds: [
          Enum.PageType.ACTIVITY_APPLICATION,
          Enum.PageType.FREE_GOODS_APPLICATION,
        ].join(),
      });
      if (pageTypeResp && pageTypeResp.data) {
        setPageTypes(pageTypeResp.data);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (props.data) {
        const formResp = await FormApi.get(props.data.relatedFormId);
        formRef.current?.setFieldsValue({
          pageTypeId: formResp.data.pageTypeId,
        });
        setEnable(false);
        handlePageTypeIdChange(formResp.data.pageTypeId);
      } else if (pageTypes) {
        formRef.current?.setFieldsValue({
          pageTypeId: pageTypes[0].pageTypeId,
          remark: pageTypes[0].remark,
        });
      }
    })();
  }, [pageTypes]);

  return (
    <WdiModalForm
      {...HorzLayoutOpts}
      formRef={formRef}
      title={props.title}
      visible={props.visible}
      modalProps={{
        onCancel: () => {
          props.onClose?.();
        },
      }}
      onFinish={async (values) => {
        const {budgetProject, ...entity} = values;
        if (needProject) {
          entity.budgetProjectId = budgetProject.budgetProjectId;
        }
        entity.relatedFormId = props?.data?.relatedFormId
        entity.relationType = props?.data?.relationType
        entity.formTypeId = Enum.FormType.ACTIVITY_APPLICATION;

        const response = await FormSa.myActivityPrev(entity);
        if (props.onSave) props.onSave?.(response);
      }}
    >
      <Form.Item
        name="pageTypeId"
        label="表单类型"
        rules={[{required: true, message: '表单类型不能为空!'}]}
      >
        <WdiSelect
          {...{
            placeholder: '请选择表单类型',
            showSearch: true,
            disabled: !enable,
            options: (pageTypes ?? []).map((item) => ({
              value: item.pageTypeId,
              label: item.pageTypeName,
            })),
            onChange: handlePageTypeIdChange,
          }}
        />
      </Form.Item>
      {needProject && (
        <Form.Item
          name="budgetProject"
          label="专项项目"
          rules={[{required: true, message: '专项项目不能为空!'}]}
        >
          <ProjectSelectApp/>
        </Form.Item>
      )}
      <Form.Item name="remark" label="描述">
        <Input.TextArea rows={3} readOnly/>
      </Form.Item>
    </WdiModalForm>
  );
};
export default MyActivityPrevApp;

export const MyPdActivityPrevApp: React.FC<WdiModalAppProps> = (props) => {
  const formRef = useRef<ProFormInstance>();
  const [revTypeOpts, setRevTypeOpts] = useState<any[]>();

  useEffect(() => {
    Promise.all([
      Position.access({useCaseId: Enum.UseCase.SA, operateTypeIds: [Enum.OperateType.APPLY_PD_DISCOUNT]}),
      Position.access({useCaseId: Enum.UseCase.SA, operateTypeIds: [Enum.OperateType.APPLY_PD_BUY_GIVE]}),
      RevenueType.search({applyType: Enum.RevApplyType.SA_PD}),
    ]).then(responseArray => {
      const allowApplyDiscount = responseArray[0]?.data ?? false;
      const allowApplyBuyGive = responseArray[1]?.data ?? false;
      const revenueTypeResp = responseArray[2];
      if (revenueTypeResp && revenueTypeResp.data) {
        const revenueTypes = revenueTypeResp.data.filter(item =>
          (allowApplyDiscount && item.revenueTypeId >= 401 && item.revenueTypeId < 410) ||
          (allowApplyBuyGive && item.revenueTypeId >= 411 && item.revenueTypeId < 420)
        )
        if (revenueTypes.length === 1) {
          formRef.current?.setFieldsValue({revenueType: revenueTypes[0].revenueTypeId});
        }
        setRevTypeOpts(revenueTypes.map(item => ({value: item.revenueTypeId, label: item.revenueTypeName})));
      }
    });
  }, []);

  return (
    <WdiModalForm
      {...HorzLayoutOpts}
      formRef={formRef}
      title={props.title}
      visible={props.visible}
      modalProps={{
        onCancel: () => {
          props.onClose?.();
        },
      }}
      onFinish={async (values) => {
        const {revenueType} = values;
        props.onSave?.({
          code: ResponseStatus.SUCCESS,
          data: {revenueType: revenueType},
        });
      }}
    >
      <Form.Item name="revenueType" label="核算类型" rules={[{required: true, message: '核算类型不能为空!'}]}>
        <WdiSelect {...{
          placeholder: "请选择核算类型",
          showSearch: true,
          options: revTypeOpts,
        }} />
      </Form.Item>
    </WdiModalForm>
  );
};
